/* eslint-disable no-nested-ternary */
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Blob } from '@/services/types/blob-types';

import { LoadingDot } from './LoadingSkeleton';

interface CitationThumbnailProps {
  citationId?: string;
  previewUrl?: string;
  blob?: Blob;
  isFullWidth?: boolean;
}

// State management with useReducer for better performance
interface ThumbnailState {
  isExpanded: boolean;
  isHovered: boolean;
}

type ThumbnailAction =
  | { type: 'SET_EXPANDED'; payload: boolean }
  | { type: 'SET_HOVERED'; payload: boolean };

const thumbnailReducer = (
  state: ThumbnailState,
  action: ThumbnailAction,
): ThumbnailState => {
  switch (action.type) {
    case 'SET_EXPANDED':
      return { ...state, isExpanded: action.payload };
    case 'SET_HOVERED':
      return { ...state, isHovered: action.payload };
    default:
      return state;
  }
};

const initialState: ThumbnailState = {
  isExpanded: false,
  isHovered: false,
};

// Custom hooks inline for better maintainability
const useIntersectionObserver = (
  ref: React.RefObject<Element>,
  options: { threshold?: number; rootMargin?: string } = {},
): boolean => {
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px', ...options },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [ref, options.threshold, options.rootMargin]);

  return isInView;
};

interface ImageDimensions {
  width: number;
  height: number;
}

const useImageLoader = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [naturalDimensions, setNaturalDimensions] =
    useState<ImageDimensions | null>(null);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setIsLoading(false);
      setHasLoaded(true);
      const img = e.currentTarget;
      setNaturalDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    },
    [],
  );

  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setImageError(true);
  }, []);

  return {
    isLoading,
    hasLoaded,
    imageError,
    naturalDimensions,
    handleImageLoad,
    handleImageError,
  };
};

interface Position {
  x: number;
  y: number;
}

const useZoomPan = () => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoomLevel > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - panPosition.x,
          y: e.clientY - panPosition.y,
        });
      }
    },
    [zoomLevel, panPosition],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoomLevel > 1) {
        setPanPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, zoomLevel, dragStart],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY * -0.01;
      const newZoom = Math.min(Math.max(0.5, zoomLevel + delta), 3);

      if (newZoom <= 1) {
        setPanPosition({ x: 0, y: 0 });
      }

      setZoomLevel(newZoom);
    },
    [zoomLevel],
  );

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  return {
    zoomLevel,
    panPosition,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    resetZoom,
  };
};

// Extracted components for better organization and performance
const NoPreviewState = ({
  containerRef,
  isFullWidth,
  blob,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  isFullWidth: boolean;
  blob?: Blob;
}) => {
  const containerHeight = isFullWidth ? 'h-60' : 'h-40'; // Matches fixed heights

  return (
    <div
      ref={containerRef}
      className={`w-full ${containerHeight} bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg flex items-center justify-center transition-all duration-200 hover:shadow-sm hover:border-gray-300`}
    >
      <div className="text-center">
        <div className="text-3xl mb-2">{blob ? '🖼️' : '📄'}</div>
        <span className="text-xs text-gray-500 font-medium">No preview</span>
      </div>
    </div>
  );
};

const ThumbnailContainer = ({
  containerRef,
  containerStyles,
  isHovered,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onKeyDown,
  ariaLabel,
  children,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  containerStyles: React.CSSProperties;
  isHovered: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  ariaLabel: string;
  children: React.ReactNode;
}) => (
  <div
    ref={containerRef}
    className={`
      relative bg-white border rounded-lg overflow-hidden 
      transition-all duration-200 ease-out cursor-pointer
      ${
        isHovered
          ? 'border-blue-300 shadow-lg ring-2 ring-blue-500/10'
          : 'border-gray-200 shadow-sm hover:border-gray-300 hover:shadow-md'
      }
      focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500
    `}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={containerStyles}
    role="button"
    tabIndex={0}
    onKeyDown={onKeyDown}
    aria-label={ariaLabel}
  >
    {children}

    {/* Hover overlay with expand hint */}
    <div
      className={`
        absolute inset-0 bg-black/0 transition-all duration-200
        flex items-center justify-center pointer-events-none
        ${isHovered ? 'bg-black/5' : ''}
      `}
    >
      <div
        className={`
          bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-sm
          transition-all duration-200 transform
          ${isHovered ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}
        `}
      >
        <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6"
            />
          </svg>
          Click to expand
        </span>
      </div>
    </div>
  </div>
);

const ThumbnailContent = ({
  isInView,
  imageUrl,
  altText,
  isLoading,
  hasLoaded,
  isFullWidth,
  isHovered,
  onImageLoad,
  onImageError,
}: {
  isInView: boolean;
  imageUrl: string;
  altText: string;
  isLoading: boolean;
  hasLoaded: boolean;
  isFullWidth: boolean;
  isHovered: boolean;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onImageError: () => void;
}) => (
  <div className="relative overflow-hidden rounded-lg h-full">
    {!hasLoaded && (
      <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-pulse" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-gray-400">
            <svg
              className="w-8 h-8 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        </div>
      </div>
    )}

    {isLoading && isInView && (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50/90 to-gray-100/90 backdrop-blur-sm">
        <div className="relative">
          <LoadingDot />
          <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-pulse" />
        </div>
      </div>
    )}

    {isInView && (
      <img
        src={imageUrl}
        alt={altText}
        className={`
          w-full h-full object-contain transition-all duration-300 ease-out
          ${!hasLoaded ? 'opacity-0 blur-lg scale-110' : 'opacity-100 blur-0 scale-100'}
          ${isHovered ? 'brightness-[1.02]' : ''}
        `}
        onLoad={onImageLoad}
        onError={onImageError}
      />
    )}
  </div>
);

const ImageModal = ({
  isExpanded,
  onClose,
  imageUrl,
  altText,
  displayName,
  blob,
  zoomLevel,
  panPosition,
  isDragging,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onWheel,
  onReset,
  onImageError,
}: {
  isExpanded: boolean;
  onClose: () => void;
  imageUrl: string;
  altText: string;
  displayName?: string;
  blob?: Blob;
  zoomLevel: number;
  panPosition: { x: number; y: number };
  isDragging: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onWheel: (e: React.WheelEvent) => void;
  onReset: () => void;
  onImageError: () => void;
}) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'r' || e.key === 'R') {
        onReset();
      }
    },
    [onReset],
  );

  const isResetDisabled =
    zoomLevel === 1 && panPosition.x === 0 && panPosition.y === 0;

  return (
    <Dialog open={isExpanded} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] p-0 overflow-hidden bg-black/95 border-none">
        <div className="relative w-full h-full min-h-[80vh]">
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent p-4">
            <div className="flex items-center justify-between text-white">
              <div className="space-y-1">
                <h3 className="text-lg font-medium">
                  {blob ? 'Image File' : 'Document Section'}
                </h3>
                <span className="text-xs text-white/70 font-mono bg-white/10 px-2 py-1 rounded">
                  {displayName}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <div className="text-xs text-white/70">
                  Zoom: {Math.round(zoomLevel * 100)}%
                </div>
                <button
                  type="button"
                  onClick={onReset}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isResetDisabled}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="w-full h-full flex items-center justify-center p-8 pt-20 cursor-grab active:cursor-grabbing bg-transparent border-0 outline-none"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onWheel={onWheel}
            onKeyDown={handleKeyDown}
            aria-label="Zoomable image viewer. Use mouse wheel to zoom, drag to pan, or press R to reset."
          >
            <img
              src={imageUrl}
              alt={altText}
              className="max-w-full max-h-full object-contain transition-transform duration-200 ease-out select-none"
              style={{
                transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                cursor:
                  zoomLevel > 1
                    ? isDragging
                      ? 'grabbing'
                      : 'grab'
                    : 'default',
              }}
              onError={onImageError}
              draggable={false}
            />
          </button>

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
            <div className="bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2 text-white/70 text-xs">
              Scroll to zoom • Drag to pan • ESC to close
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CitationThumbnail = ({
  citationId,
  previewUrl,
  blob,
  isFullWidth = false,
}: CitationThumbnailProps) => {
  const [state, dispatch] = useReducer(thumbnailReducer, initialState);
  const containerRef = useRef<HTMLDivElement>(null);

  // Custom hooks for specific functionality
  const isInView = useIntersectionObserver(containerRef, {
    threshold: 0.1,
    rootMargin: '50px',
  });

  const { imageUrl, displayName, ariaLabel, altText } = useMemo(() => {
    const url = blob?.download_url || previewUrl;
    const name =
      blob?.original_filename || blob?.name || citationId?.split('/').pop();
    const label = blob
      ? `View image ${blob.original_filename || blob.name}`
      : `View document section ${citationId?.split('/').pop()}`;
    const alt = blob
      ? blob.original_filename || blob.name
      : `Document section ${citationId?.split('/').pop()}`;

    return { imageUrl: url, displayName: name, ariaLabel: label, altText: alt };
  }, [blob, previewUrl, citationId]);

  const {
    isLoading,
    hasLoaded,
    imageError,
    naturalDimensions,
    handleImageLoad,
    handleImageError,
  } = useImageLoader();

  const {
    zoomLevel,
    panPosition,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    resetZoom,
  } = useZoomPan();

  // Memoized styles calculation
  const containerStyles = useMemo(() => {
    if (isFullWidth) {
      return {
        width: '100%',
        height: '240px', // Fixed height for consistency
      } as const;
    }

    // Consistent sizing for single-column layout
    return {
      width: '100%',
      height: '160px', // Fixed height for all thumbnails
    } as const;
  }, [isFullWidth]);

  // Memoized event handlers
  const handleClick = useCallback(() => {
    if (imageUrl && !imageError && hasLoaded) {
      dispatch({ type: 'SET_EXPANDED', payload: true });
      resetZoom();
    }
  }, [imageUrl, imageError, hasLoaded, resetZoom]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleMouseEnter = useCallback(() => {
    dispatch({ type: 'SET_HOVERED', payload: true });
  }, []);

  const handleMouseLeave = useCallback(() => {
    dispatch({ type: 'SET_HOVERED', payload: false });
  }, []);

  const closeModal = useCallback(() => {
    dispatch({ type: 'SET_EXPANDED', payload: false });
  }, []);

  if (!imageUrl || imageError) {
    return (
      <NoPreviewState
        containerRef={containerRef}
        isFullWidth={isFullWidth}
        blob={blob}
      />
    );
  }

  return (
    <>
      <ThumbnailContainer
        containerRef={containerRef}
        containerStyles={containerStyles}
        isHovered={state.isHovered}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        ariaLabel={ariaLabel}
      >
        <ThumbnailContent
          isInView={isInView}
          imageUrl={imageUrl}
          altText={altText}
          isLoading={isLoading}
          hasLoaded={hasLoaded}
          isFullWidth={isFullWidth}
          isHovered={state.isHovered}
          onImageLoad={handleImageLoad}
          onImageError={handleImageError}
        />
      </ThumbnailContainer>

      <ImageModal
        isExpanded={state.isExpanded}
        onClose={closeModal}
        imageUrl={imageUrl}
        altText={altText}
        displayName={displayName}
        blob={blob}
        zoomLevel={zoomLevel}
        panPosition={panPosition}
        isDragging={isDragging}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        onReset={resetZoom}
        onImageError={() => handleImageError()}
      />
    </>
  );
};
